import { useEffect, useState } from "react";

import { getTokenClaims } from "../authConfig";
import { useAuthClient, type AuthContext } from "../useAuthClient";

const ADMIN_PERMISSION = "admin:dev-console";
const ADMIN_ROLE_VALUES = ["admin", "uadmin"];
const KNOWN_PERMISSION_KEYS = [
    "permissions",
    "https://atlas.ai/permissions",
    "https://schemas.atlas.ai/permissions",
    "https://harborglobal.com/claims/permissions"
];
const KNOWN_ROLE_KEYS = [
    "roles",
    "role",
    "appRoles",
    "https://schemas.microsoft.com/ws/2008/06/identity/claims/role",
    "https://harborglobal.com/claims/roles"
];

const normalizeStringArray = (value: unknown): string[] => {
    if (!value) {
        return [];
    }
    if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === "string");
    }
    if (typeof value === "string") {
        return value
            .split(/[\s,]+/)
            .map(entry => entry.trim())
            .filter(Boolean);
    }
    return [];
};

const collectValues = (source: Record<string, unknown> | undefined, keys: string[]): string[] => {
    if (!source) {
        return [];
    }
    return keys.flatMap(key => normalizeStringArray(source[key]));
};

export const evaluateAdminStatus = (claims: Record<string, unknown> | undefined, user: Record<string, unknown> | undefined) => {
    const claimPermissions = collectValues(claims, KNOWN_PERMISSION_KEYS);
    const userPermissions = collectValues(user, KNOWN_PERMISSION_KEYS);
    const permissions = [...claimPermissions, ...userPermissions];

    const claimRoles = collectValues(claims, KNOWN_ROLE_KEYS);
    const userRoles = collectValues(user, KNOWN_ROLE_KEYS);
    const roles = [...claimRoles, ...userRoles];

    const hasLocalOverride = (() => {
        if (typeof window === "undefined") {
            return false;
        }
        return window.localStorage?.getItem("atlasDevConsole") === "1";
    })();

    return permissions.includes(ADMIN_PERMISSION) || roles.some(role => ADMIN_ROLE_VALUES.includes(role.toLowerCase())) || hasLocalOverride;
};

export const useAdminAccess = (authOverride?: AuthContext) => {
    const authFromHook = useAuthClient();
    const auth = authOverride ?? authFromHook;
    const [isAdmin, setIsAdmin] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;
        const resolve = async () => {
            try {
                const claims = await getTokenClaims(auth);
                if (!isMounted) {
                    return;
                }
                const derived = evaluateAdminStatus(claims, (auth?.user as Record<string, unknown>) ?? undefined);
                setIsAdmin(derived);
            } catch (err) {
                if (isMounted) {
                    setIsAdmin(false);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        resolve();
        return () => {
            isMounted = false;
        };
    }, [auth]);

    return { isAdmin, isLoading } as const;
};
